// Manual and automatic calculation organization, including persistence and the
// shared overview/sidebar projection. Fixture counts come from the capture data.
import type { HelpArticle } from "../../types";
import {
  REGISTER_FIXTURE,
  REGISTER_GROUP_COUNTS,
} from "../../../calculations/calcRegisterFixtures";

const groupLabel = (id: string): string =>
  REGISTER_FIXTURE.groups.find((group) => group.id === id)?.label ?? "Ungrouped";

export const organizingCalculationsArticle: HelpArticle = {
  id: "organizing-calculations",
  title: "Organizing calculations",
  description:
    "Choose manual project groups or automatic calculator-family groups, move sheets without changing them, and understand how the same organization follows the project sidebar.",
  tags: ["groups", "manual", "automatic", "sidebar", "organization", "ungrouped"],
  lastUpdated: "July 19, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Organization changes how a project's sheets are arranged, never what they calculate. Manual mode follows the drawing set or load path you name. Automatic mode follows Firmark's calculator families. Both are views over the same persisted calculations, results, and marks.",
    },

    { kind: "heading", level: 2, text: "Choose the organizing rule", id: "modes" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "Overview", "Groups"] },
    {
      kind: "paragraph",
      text: "Switch the register from **Table** to **Groups**, then choose **Manual** or **Automatic** under Organization. A new project defaults to Manual with an empty group list. The mode is saved in the calculation project's settings, so another browser opening the same project receives the same choice from the server.",
    },
    {
      kind: "shot",
      shotId: "calc-groups-manual",
      caption: `Manual mode in the fixture: ${groupLabel("level-2")} holds ${REGISTER_GROUP_COUNTS["level-2"]} calculations, with row-level controls for moving them.`,
    },
    {
      kind: "values",
      caption: "The manual grouping fixture shared with the figures.",
      rows: [
        { label: groupLabel("level-2"), value: `${REGISTER_GROUP_COUNTS["level-2"]} calculations` },
        { label: groupLabel("vertical"), value: `${REGISTER_GROUP_COUNTS.vertical} calculations` },
        { label: groupLabel("connections"), value: `${REGISTER_GROUP_COUNTS.connections} calculation` },
        { label: "Ungrouped", value: `${REGISTER_GROUP_COUNTS.ungrouped} calculation` },
      ],
    },
    {
      kind: "paragraph",
      text: "Each group header carries its own rollup: count, worst numeric DCR when one exists, and the strongest status among its members. Failing wins over partial or near-limit work; pending wins over current. Collapsing a group only closes its rows for the current view.",
    },

    { kind: "heading", level: 2, text: "Build a manual register", id: "manual" },
    {
      kind: "steps",
      steps: [
        {
          title: "Create the named group",
          body: "Choose **New group**, enter a nonblank name, and create it. The editor starts with `Level 2 framing` as an example, and leading or trailing whitespace is trimmed before the label is saved.",
        },
        {
          title: "Assign calculations",
          body: "Use the destination select on a row, or open that row's ellipsis manager and choose Group. Selecting **Ungrouped** removes the assignment; the calculation itself stays in the project.",
        },
        {
          title: "Maintain the group",
          body: "Rename changes the group label without touching its membership. Delete opens a destructive-looking confirmation because the group disappears, but the dialog states the consequence precisely: its calculations move to Ungrouped and no calculations are deleted.",
        },
      ],
    },
    {
      kind: "paragraph",
      text: "A duplicate made from the calculation manager inherits the source's current manual group. A newly created calculation from the catalog has no assignment and appears under Ungrouped until you place it. If a sheet is deleted elsewhere, the server removes its stale assignment when the next organization edit is saved.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "Manual groups are project data",
      body: "The API stores the complete mode, ordered group list, and sheet-to-group assignments on the calculation project. The browser keeps a synchronous cache so the overview and project sidebar update together; the server snapshot remains authoritative across browsers.",
    },

    { kind: "heading", level: 2, text: "Let calculator families organize", id: "automatic" },
    {
      kind: "paragraph",
      text: "Automatic mode ignores manual assignments while it is active and derives membership from each sheet's calculator id. Switching back to Manual restores the named groups and assignments because changing mode does not erase them.",
    },
    {
      kind: "shot",
      shotId: "calc-groups-automatic",
      caption: "The same eight sheets arranged automatically. Four wood-beam sheets become Beams & joists; the MWFRS draft becomes Wind.",
    },
    {
      kind: "table",
      caption: "The fixed automatic register families.",
      headers: ["Family", "What lands there"],
      rows: [
        ["Beams & joists", "Wood beam, SCL beam, and I-joist calculators"],
        ["Walls & studs", "Wood stud-wall calculations"],
        ["Columns", "Wood column and biaxial beam-column calculations"],
        ["Tension", "Wood tension members"],
        ["Connections", "Dowel fastening, product capacity, hanger, and hold-down calculations"],
        ["Steel / Concrete / Masonry / Analysis", "Registry-driven structural calculators by material or analysis family"],
        ["Wind / Snow / Seismic / Gravity / Live loads / Rain & ice", "Manual load-calculation families"],
        ["Other calculations", "A future calculator id that has no declared family remains visible here"],
      ],
    },
    {
      kind: "paragraph",
      text: "Automatic groups are not editable. Their labels, membership rules, and order come from the product's grouping registry. The row-level move control and group Rename / Delete actions disappear because there is no project assignment to change.",
    },

    { kind: "heading", level: 2, text: "The sidebar follows the register", id: "sidebar" },
    {
      kind: "paragraph",
      text: "The calculation-project sidebar reads the same saved organization. It names the active mode beside **Calculations**, renders only groups that contain sheets, and lets each group collapse independently. Every sheet link keeps the mark, section or load-calculation short name, and a status-readable value.",
    },
    {
      kind: "shot",
      shotId: "calc-groups-sidebar",
      caption: "Manual organization projected into the project sidebar. Overview, New calculation, and every sheet stay inside one project navigation slot.",
    },
    {
      kind: "paragraph",
      text: "Search and status filtering on the overview operate after organization is loaded. They can make a group temporarily empty in the register without removing its assignments. The sidebar is not filtered by the register's search box, so navigation to the full project remains intact while you narrow the review table.",
    },

    { kind: "related", topics: ["calculation-register", "managing-calculations", "sheet-lifecycle"] },
  ],
};
